import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getGodsQuestions, RELIGIONS } from '../utils/godsQuestions';
import { playCorrectSound, playWrongSound } from '../utils/soundEffects';
import { X, ChevronRight, Sparkles, Lightbulb } from 'lucide-react';
import confetti from 'canvas-confetti';

const OPTION_COLORS = [
  'bg-orange-500 border-orange-700 hover:bg-orange-400',
  'bg-blue-500 border-blue-700 hover:bg-blue-400',
  'bg-amber-500 border-amber-700 hover:bg-amber-400',
  'bg-emerald-500 border-emerald-700 hover:bg-emerald-400',
];

export default function GodsPlayPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const religionId = searchParams.get('religion') || 'hinduism';
  const religion = RELIGIONS.find((r) => r.id === religionId) || RELIGIONS[0];

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [hintsLeft, setHintsLeft] = useState(10);
  const [removedOptions, setRemovedOptions] = useState([]);
  const scoreRef = useRef(0);

  useEffect(() => { scoreRef.current = score; }, [score]);

  useEffect(() => {
    setQuestions(getGodsQuestions(religion.id, 20));
    setCurrentIndex(0);
    setScore(0);
    setSelectedOption(null);
    setShowAnswer(false);
    setHintsLeft(10);
    setRemovedOptions([]);
  }, [religion.id]);

  const currentQ = questions[currentIndex];

  const handleHint = () => {
    if (showAnswer || hintsLeft <= 0 || removedOptions.length > 0) return;
    const wrong = currentQ.options
      .map((_, i) => i)
      .filter((i) => i !== currentQ.correctIndex)
      .sort(() => Math.random() - 0.5)
      .slice(0, 2);
    setRemovedOptions(wrong);
    setHintsLeft((h) => h - 1);
  };

  const handleOptionClick = (idx) => {
    if (showAnswer) return;
    setSelectedOption(idx);
    setShowAnswer(true);

    if (idx === currentQ.correctIndex) {
      setScore((s) => s + 1);
      playCorrectSound();
      confetti({ particleCount: 50, spread: 60, origin: { y: 0.7 }, colors: ['#f97316', '#3b82f6', '#eab308', '#10b981'] });
    } else {
      playWrongSound();
    }
  };

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      navigate(`/gods/results?score=${scoreRef.current}&total=${questions.length}&religion=${religion.id}`);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setSelectedOption(null);
    setShowAnswer(false);
    setRemovedOptions([]);
  };

  if (!currentQ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white">
        <p className="font-heading text-2xl text-gray-400">Loading...</p>
      </div>
    );
  }

  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-orange-50 flex flex-col">
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <span data-testid="gods-score" className="font-heading text-2xl font-black text-gray-900">
              {score}/{questions.length}
            </span>
          </div>
          <span data-testid="gods-counter" className="font-body text-sm font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {currentIndex + 1} of {questions.length}
          </span>
          <button data-testid="gods-quit" onClick={() => navigate('/gods')} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      <div className="h-2 bg-gray-200">
        <div
          data-testid="gods-progress"
          className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-r-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-6">
        <div className="text-center mb-3">
          <span
            data-testid="gods-religion-badge"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full text-white"
            style={{ background: religion.gradient }}
          >
            <span aria-hidden="true" className="text-base leading-none">{religion.symbol}</span>
            {religion.name}
          </span>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6 text-center">
          <p
            data-testid="gods-question"
            className="font-body text-lg sm:text-xl text-gray-800 leading-relaxed font-bold"
          >
            {currentQ.question}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {currentQ.options.map((opt, i) => {
            const isRemoved = removedOptions.includes(i);
            let extraClass = '';
            if (showAnswer) {
              if (i === currentQ.correctIndex) {
                extraClass = 'ring-4 ring-green-400 scale-[1.02]';
              } else if (i === selectedOption) {
                extraClass = 'opacity-60 ring-4 ring-red-400';
              } else {
                extraClass = 'opacity-40';
              }
            } else if (isRemoved) {
              extraClass = 'opacity-25 line-through grayscale cursor-not-allowed';
            }

            return (
              <button
                key={`${currentQ.id}-opt-${opt}`}
                data-testid={`gods-option-${i}`}
                onClick={() => handleOptionClick(i)}
                disabled={showAnswer || isRemoved}
                className={`${OPTION_COLORS[i]} text-white font-bold text-sm sm:text-base py-4 px-4 rounded-2xl border-b-[5px] ${!showAnswer && !isRemoved ? 'active:border-b-0 active:translate-y-[5px]' : ''} transition-all shadow-md text-center ${extraClass}`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {!showAnswer && (
          <div className="text-center mb-4">
            <button
              data-testid="gods-hint"
              onClick={handleHint}
              disabled={hintsLeft <= 0 || removedOptions.length > 0}
              className="inline-flex items-center gap-2 bg-white text-amber-700 font-black text-sm py-2.5 px-6 rounded-full border-2 border-amber-300 shadow-sm hover:bg-amber-50 hover:border-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <Lightbulb className="w-5 h-5" />
              {removedOptions.length > 0 ? 'Hint used' : 'Use a Hint'}
              <span data-testid="gods-hints-left" className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full">
                {hintsLeft} left
              </span>
            </button>
            <p className="font-body text-xs text-gray-500 mt-2">A hint removes two wrong answers. You get 10 hints per round.</p>
          </div>
        )}

        {showAnswer && (
          <div className="text-center animate-pop-in">
            {selectedOption === currentQ.correctIndex ? (
              <p data-testid="gods-feedback-correct" className="font-heading text-xl font-black text-green-600 mb-3">
                Correct!
              </p>
            ) : (
              <p data-testid="gods-feedback-wrong" className="font-heading text-base font-bold text-red-500 mb-3">
                It&apos;s <span className="text-green-700">{currentQ.answer}</span>!
              </p>
            )}
            <button
              data-testid="gods-next"
              onClick={handleNext}
              className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-black text-base py-3 px-8 rounded-full border-b-[4px] border-orange-800 active:border-b-0 active:translate-y-[4px] transition-all hover:from-amber-400 hover:to-orange-500 inline-flex items-center gap-2"
            >
              {currentIndex + 1 >= questions.length ? 'See Results' : 'Next Question'}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
